import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, FileText, Users, TrendingUp, TrendingDown, Plus, DollarSign, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, cn } from '../utils/formatters';
import { CardSkeleton } from '../components/ui/LoadingScreen';

const statusConfig: Record<string, { label: string; pill: string }> = {
  planned: { label: 'Planificado', pill: '' },
  in_progress: { label: 'En curso', pill: 'b2b-pill--accent' },
  stopped: { label: 'Detenido', pill: 'b2b-pill--warn' },
  finished: { label: 'Finalizado', pill: 'b2b-pill--ok' },
};

const budgetStatusConfig: Record<string, { label: string; pill: string }> = {
  draft: { label: 'Borrador', pill: '' },
  sent: { label: 'Enviado', pill: 'b2b-pill--warn' },
  accepted: { label: 'Aceptado', pill: 'b2b-pill--ok' },
  rejected: { label: 'Rechazado', pill: 'b2b-pill--danger' },
  expired: { label: 'Expirado', pill: '' },
};

export function DashboardPage() {
  const { user } = useAuth();
  const { company, projects, clients, budgets, expenses, isLoading } = useCompany();
  const [stats, setStats] = useState({ activeProjects: 0, pendingBudgets: 0, monthlyExpenses: 0, monthlyIncome: 0 });

  useEffect(() => {
    if (isLoading) return;
    const active = projects.filter(p => p.status === 'in_progress').length;
    const pending = budgets.filter(b => b.status === 'sent' || b.status === 'draft').length;
    const currentMonth = new Date().toISOString().slice(0, 7);
    const monthlyExp = expenses.filter(e => e.date.startsWith(currentMonth)).reduce((s, e) => s + e.total, 0);
    const monthlyInc = budgets
      .filter(b => b.status === 'accepted' && b.issueDate.startsWith(currentMonth))
      .reduce((s, b) => s + b.total, 0);
    setStats({ activeProjects: active, pendingBudgets: pending, monthlyExpenses: monthlyExp, monthlyIncome: monthlyInc });
  }, [projects, budgets, expenses, isLoading]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const recentBudgets = [...budgets]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const kpis = [
    { label: 'Proyectos activos', value: String(stats.activeProjects), icon: Hammer, accent: true },
    { label: 'Presupuestos pendientes', value: String(stats.pendingBudgets), icon: FileText, accent: false },
    { label: 'Gastos este mes', value: formatCurrency(stats.monthlyExpenses), icon: TrendingDown, accent: false },
    { label: 'Ingresos este mes', value: formatCurrency(stats.monthlyIncome), icon: TrendingUp, accent: false },
  ];

  const quickActions = [
    { name: 'Nuevo Proyecto', to: '/projects/new', icon: Hammer },
    { name: 'Nuevo Presupuesto', to: '/budgets/new', icon: FileText },
    { name: 'Registrar Gasto', to: '/expenses/new', icon: DollarSign },
    { name: 'Nuevo Cliente', to: '/clients/new', icon: Users },
  ];

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Panel de control</span>
          <h1 className="text-3xl font-extrabold tracking-tight">
            Hola, {user?.name?.split(' ')[0] || 'Usuario'}
          </h1>
          <p className="b2b-muted">Resumen de tu actividad en {company?.name || 'tu empresa'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/projects/new" className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" /> Nuevo Proyecto
          </Link>
          <Link to="/budgets/new" className="b2b-btn b2b-btn--ghost">
            <FileText className="w-4 h-4" /> Nuevo Presupuesto
          </Link>
        </div>
      </div>

      {/* KPIs (Bento) */}
      <div className="b2b-bento">
        {kpis.map(kpi => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className={cn('b2b-card b2b-col-3', kpi.accent && 'b2b-card--accent')}>
              <div className="b2b-card__head">
                <span className="b2b-icon"><Icon className="w-5 h-5" /></span>
                <span className="b2b-card__label">{kpi.label}</span>
              </div>
              <span className="b2b-card__value">{kpi.value}</span>
            </div>
          );
        })}
      </div>

      {/* Acciones rápidas */}
      <div className="b2b-bento">
        {quickActions.map(action => {
          const Icon = action.icon;
          return (
            <Link key={action.name} to={action.to} className="b2b-card b2b-card--row b2b-col-3">
              <span className="b2b-icon"><Icon className="w-5 h-5" /></span>
              <span className="font-medium">{action.name}</span>
              <ChevronRight className="w-4 h-4 b2b-muted ml-auto" />
            </Link>
          );
        })}
      </div>

      {/* Actividad reciente */}
      <div className="b2b-bento">
        <div className="b2b-panel b2b-col-6">
          <div className="b2b-panel__head">
            <h2 className="text-lg font-semibold">Proyectos recientes</h2>
            <Link to="/projects" className="text-sm font-medium text-[color:var(--b2b-accent-strong)]">Ver todos</Link>
          </div>
          {recentProjects.length === 0 ? (
            <div className="p-8 text-center b2b-muted">
              <Hammer className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No hay proyectos aún</p>
            </div>
          ) : (
            recentProjects.map(project => {
              const client = clients.find(c => c.id === project.clientId);
              const config = statusConfig[project.status] || statusConfig.planned;
              return (
                <Link key={project.id} to={`/projects/${project.id}`} className="b2b-panel__row">
                  <span className="b2b-icon"><Hammer className="w-5 h-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium truncate">{project.name}</span>
                    <span className="block text-sm b2b-muted truncate">{client?.name || 'Sin cliente'}</span>
                  </span>
                  <span className={cn('b2b-pill', config.pill)}>{config.label}</span>
                </Link>
              );
            })
          )}
        </div>

        <div className="b2b-panel b2b-col-6">
          <div className="b2b-panel__head">
            <h2 className="text-lg font-semibold">Presupuestos recientes</h2>
            <Link to="/budgets" className="text-sm font-medium text-[color:var(--b2b-accent-strong)]">Ver todos</Link>
          </div>
          {recentBudgets.length === 0 ? (
            <div className="p-8 text-center b2b-muted">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No hay presupuestos aún</p>
            </div>
          ) : (
            recentBudgets.map(budget => {
              const client = clients.find(c => c.id === budget.clientId);
              const config = budgetStatusConfig[budget.status] || budgetStatusConfig.draft;
              return (
                <Link key={budget.id} to={`/budgets/${budget.id}`} className="b2b-panel__row">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{budget.number}</span>
                      <span className={cn('b2b-pill', config.pill)}>{config.label}</span>
                    </div>
                    <span className="block text-sm b2b-muted truncate">{client?.name || 'Sin cliente'}</span>
                  </div>
                  <span className="font-semibold whitespace-nowrap">{formatCurrency(budget.total)}</span>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
